import { ILicenseRights, hasOwnProperty, hasReplacedLicenseRight, hasWebSelfHostedRight } from '../../tn.common';

function _hasWeb(fontRight) {
    return (
        fontRight.web &&
        hasOwnProperty(fontRight.web, 'qty') &&
        hasOwnProperty(fontRight.web, 'years') &&
        fontRight.web.qty !== null &&
        fontRight.web.years !== null
    );
}

function _hasWebHosted(fontRight) {
    return (
        fontRight.web &&
        fontRight.web.active &&
        fontRight.web.qty &&
        !fontRight.web.self_hosted
    );
}

// @deprecated, import and use hasWebSelfHostedRight function instead
function _hasWebSelfHosted(fontRight: ILicenseRights) {
    return hasWebSelfHostedRight(fontRight);
}

// @deprecated, import and use hasReplacedLicenseRight function instead
function _hasReplacedLicense(fontRight: ILicenseRights) {
    return hasReplacedLicenseRight(fontRight);
}

function _hasUpgradedOrRenewed(fontRight: ILicenseRights) {
    return Object.values(fontRight).some(
        (right) => right.is_upgrade_or_renewal
    );
}

function _hasApp(fontRight) {
    return fontRight.app && fontRight.app.active && fontRight.app.qty;
}

function _hasBroadcast(fontRight) {
    return fontRight.broadcast && fontRight.broadcast.active;
}

function _hasDesktop(fontRight) {
    return (
        fontRight.desktop && fontRight.desktop.active && fontRight.desktop.qty
    );
}

function _hasEpub(fontRight) {
    return fontRight.epub && fontRight.epub.active && fontRight.epub.qty;
}

function _hasServer(fontRight) {
    return fontRight.server && fontRight.server.active;
}

function _hasGame(fontRight) {
    return fontRight.game && fontRight.game.active;
}

function _hasDownload(fontRight) {
    return (
        _hasApp(fontRight) ||
        _hasBroadcast(fontRight) ||
        _hasDesktop(fontRight) ||
        _hasEpub(fontRight) ||
        _hasServer(fontRight) ||
        _hasGame(fontRight) ||
        _hasWebSelfHosted(fontRight)
    );
}

// TODO: before removing ensure the non array branch is not used, I believe it may be the cart variant of the license object that branch handles.
// we'll need distinct functions for handling the cart filters vs license filters instead of this common approach.
function _listHasRight(licenseList, predicate) {
    if (Array.isArray(licenseList)) {
        return licenseList.some(function (license) {
            return predicate(license.fontRights);
        });
    } else {
        return Object.keys(licenseList).some(function (licenseIndex) {
            return predicate(licenseList[licenseIndex].fontRights);
        });
    }
}

// Get a list of all font rights a license has, and if they're a new right or an upgrade/renewal
function _getLicenseRights(license) {
    const rightList = {
        newRight: [],
        upgradeOrRenew: [],
    };
    Object.keys(rightQtyKeys).forEach((key) => {
        if (
            !hasOwnProperty(license.fontRights, key) ||
            !hasOwnProperty(license.fontRights[key], rightQtyKeys[key]) ||
            license.fontRights[key][rightQtyKeys[key]] === null
        ) {
            return;
        }
        if (license.fontRights[key].renew || license.fontRights[key].upgrade) {
            rightList.upgradeOrRenew.push(key);
        } else {
            rightList.newRight.push(key);
        }
    });

    return rightList;
}

const rightQtyKeys = {
    app: 'apps',
    desktop: 'computers',
    epub: 'ebooks',
    web: 'views',
};

function _upgradeOrRenewLicensesFilter(license) {
    return Object.keys(license.fontRights).some((licenseType) => {
        return (
            (hasOwnProperty(license.fontRights[licenseType], 'upgrade') &&
                license.fontRights[licenseType].upgrade) ||
            (hasOwnProperty(license.fontRights[licenseType], 'renew') &&
                license.fontRights[licenseType].renew)
        );
    });
}

function _newLicensesFilter(license) {
    return !_upgradeOrRenewLicensesFilter(license);
}

export class LicenseListService {
    hasWebLicense(licenseList) {
        return _listHasRight(licenseList, _hasWeb);
    }

    hasWebLicenseHosted(licenseList) {
        return _listHasRight(licenseList, _hasWebHosted);
    }

    hasWebLicenseSelfHosted(licenseList) {
        return _listHasRight(licenseList, _hasWebSelfHosted);
    }

    hasReplacedLicenses(licenseList) {
        return _listHasRight(licenseList, _hasReplacedLicense);
    }

    hasUpgradedOrRenewed(licenseList) {
        return _listHasRight(licenseList, _hasUpgradedOrRenewed);
    }

    hasAppLicense(licenseList) {
        return _listHasRight(licenseList, _hasApp);
    }

    hasEpubLicense(licenseList) {
        return _listHasRight(licenseList, _hasEpub);
    }

    hasDesktopLicense(licenseList) {
        return _listHasRight(licenseList, _hasDesktop);
    }

    hasDownloadLicense(licenseList) {
        return _listHasRight(licenseList, _hasDownload);
    }

    addLicenses(
        licenseList,
        licenses,
        types = ['app', 'desktop', 'epub', 'web'],
        allowRemoval = false
    ) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        licenses.forEach(function (license) {
            // Do not allow duplicates
            const matchingLicenses = licenseList.filter(function (licenseinCart) {
                return licenseinCart.font.id === license.font.id;
            });

            if (matchingLicenses.length === 0) {
                licenseList.push(license);
            } else {
                self.editLicenses(
                    licenseList,
                    [license],
                    license.fontRights,
                    types,
                    allowRemoval
                );
            }
        });
    }

    editLicenses(
        licenseList,
        licenses,
        licenseOptions,
        types = ['app', 'desktop', 'epub', 'web'],
        allowRemoval = false
    ) {
        const licensesIDs = licenses.map((license) => license.font.id);
        licenseList.forEach(function (license) {
            if (licensesIDs.indexOf(license.font.id) !== -1) {
                if (allowRemoval) {
                    Object.keys(rightQtyKeys).forEach((key) => {
                        // Only update non renew/upgrade fontrights
                        if (
                            !license.fontRights[key].renew &&
                            !license.fontRights[key].upgrade
                        ) {
                            license.fontRights[key] = licenseOptions[key];
                        }
                    });
                } else {
                    Object.keys(rightQtyKeys).forEach((key) => {
                        // Only update non empty fontrights
                        if (
                            types.indexOf(key) !== -1 &&
                            licenseOptions[key] &&
                            licenseOptions[key][rightQtyKeys[key]]
                        ) {
                            license.fontRights[key] = licenseOptions[key];
                        }
                    });
                }
            }
        });
    }

    removeLicense(licenseList, licenses) {
        for (let i = 0; i < licenseList.length; ) {
            const license = licenseList[i];
            if (licenses.indexOf(license) > -1) {
                licenseList.splice(i, 1);
            } else {
                i += 1;
            }
        }
    }

    removeLicenses(...args) {
        return this.removeLicense(...args);
    }

    removeFontRights(licenseList, licenses, upgradeOrRenew) {
        licenses.forEach((license) => {
            const rightList = _getLicenseRights(license);
            if (
                (upgradeOrRenew && rightList.newRight.length === 0) ||
                (!upgradeOrRenew && rightList.upgradeOrRenew.length === 0)
            ) {
                this.removeLicense(licenseList, [license]);
            } else {
                const fontRightsToRemove = upgradeOrRenew
                    ? rightList.upgradeOrRenew
                    : rightList.newRight;
                fontRightsToRemove.forEach((rightType) => {
                    if (upgradeOrRenew) {
                        if (
                            hasOwnProperty(license.fontRights[rightType],
                                'renew'
                            )
                        ) {
                            delete license.fontRights[rightType].renew;
                        }
                        if (
                            hasOwnProperty(license.fontRights[rightType],
                                'upgrade'
                            )
                        ) {
                            delete license.fontRights[rightType].upgrade;
                        }
                    }

                    Object.keys(license.fontRights[rightType]).forEach(
                        (key) => (license.fontRights[rightType][key] = null)
                    );
                });
            }
        });
    }

    getMissingLicenses(licenseList) {
        return licenseList.filter((license) => {
            const rights = license.fontRights;
            return (
                rights.app.apps === null &&
                rights.desktop.computers === null &&
                rights.epub.ebooks === null &&
                rights.web.years === null
            );
        });
    }

    getLicensesForFoundry(licenseList, foundryId) {
        return licenseList.filter((license) => {
            return license.font.foundry.indexOf(foundryId) > -1;
        });
    }

    getUpgradeOrRenewalLicenses(licenseList) {
        return licenseList.filter(_upgradeOrRenewLicensesFilter);
    }

    getNewLicenses(licenseList) {
        return licenseList.filter(_newLicensesFilter);
    }

    getLicenseTypeArray(
        isAppLicense,
        isDesktopLicense,
        isEpubLicense,
        isWebLicense
    ) {
        const typeMap = {
            app: isAppLicense,
            desktop: isDesktopLicense,
            epub: isEpubLicense,
            web: isWebLicense,
        };
        return Object.keys(typeMap).reduce((arr, key) => {
            if (typeMap[key]) {
                arr.push(key);
            }
            return arr;
        }, []);
    }
}
